import { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Search,
  Trash2,
  AlertCircle,
  ChevronDown,
  Mail,
  Phone,
  Building,
  Calendar,
  DollarSign,
  Package,
  Clock,
  FileText,
  Send,
  Save,
  Eye,
  Paperclip,
  TrendingUp,
  Info,
  Truck
} from 'lucide-react';
import jsPDF from 'jspdf';
import { useEmpresa } from '../contexts/EmpresaContext';
import '../styles/ModalNuevaCotizacion.css';

const ModalNuevaCotizacion = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState('cliente');
  const { empresaActiva, obtenerClientesPorEmpresa, obtenerProveedoresPorEmpresa } = useEmpresa();

  const [formData, setFormData] = useState({
    cliente: '',
    contacto: '',
    email: '',
    telefono: '',
    sitioEntrega: '',
    urgencia: 'normal',
    moneda: empresaActiva?.monedaDefault || 'CLP',
    metodoPago: 'transferencia',
    diasPago: empresaActiva?.diasPagoDefault ? `${empresaActiva.diasPagoDefault}dias` : '30dias',
    validez: '7',
    incluyeInstalacion: false,
    incluyeCapacitacion: false,
    fleteConIva: false,
    observacionesInternas: '',
    condicionesCliente: '',
    productos: []
  });

  const [nuevoProducto, setNuevoProducto] = useState({
    codigo: '',
    descripcion: '',
    cantidad: 1,
    unidad: 'UN',
    costo: 0,
    margen: 18,
    precio: 0,
    montoIva: 0,
    proveedor: '',
    leadTime: 15,
    ivaExtra: false
  });

  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [validaciones, setValidaciones] = useState([]);
  const [margenSugerido, setMargenSugerido] = useState(15);

  const [totales, setTotales] = useState({
    subtotal: 0,
    margenTotal: 0,
    flete: 0,
    iva: 0,
    total: 0
  });

  const [fleteManual, setFleteManual] = useState(false);

  // Obtener clientes de la empresa activa
  const clientes = empresaActiva ? obtenerClientesPorEmpresa(empresaActiva.id) : [];

  // Obtener proveedores de la empresa activa
  const proveedores = empresaActiva ? [
    { nombre: 'Stock Propio', atiende24x7: true, leadTimeBase: 1 },
    ...obtenerProveedoresPorEmpresa(empresaActiva.id)
  ] : [];

  const sitiosEntrega = [
    'Mina Escondida - Almacén Central',
    'Mina Escondida - Almacén 2',
    'División Andina - Bodega Principal',
    'Los Bronces - Recepción',
    'El Teniente - Almacén Sur',
    'Chuquicamata - Bodega Norte'
  ];

  const tarifasFlete = {
    'Mina Escondida - Almacén Central': 1200000,
    'Mina Escondida - Almacén 2': 1200000,
    'División Andina - Bodega Principal': 1800000,
    'Los Bronces - Recepción': 1500000,
    'El Teniente - Almacén Sur': 2000000,
    'Chuquicamata - Bodega Norte': 2200000
  };

  // Función para calcular margen sugerido
  const calcularMargenSugerido = (cliente, urgencia) => {
    let margenBase = 15;

    // Por categoría de cliente
    if (cliente?.categoria === 'A') margenBase = 12;
    if (cliente?.categoria === 'B') margenBase = 15;
    if (cliente?.categoria === 'C') margenBase = 18;

    // Por urgencia
    if (urgencia === 'parada') margenBase += 10;
    if (urgencia === 'urgente') margenBase += 5;

    // Por volumen
    if (totales.subtotal > 100000000) margenBase -= 2;

    return margenBase;
  };

  // Función para calcular flete inteligente
  const calcularFleteInteligente = (sitio, productos) => {
    let fletBase = tarifasFlete[sitio] || 1200000;

    // Por peso estimado (simple por ahora)
    const cantidadTotal = productos.reduce((sum, p) => sum + p.cantidad, 0);
    if (cantidadTotal > 100) fletBase += 500000;
    if (cantidadTotal > 500) fletBase += 1000000;

    return fletBase;
  };

  // Función para manejar cambio de cliente
  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.id === parseInt(clienteId));
    setClienteSeleccionado(cliente);

    if (cliente) {
      // Auto-completar campos
      setFormData(prev => ({
        ...prev,
        cliente: clienteId,
        diasPago: cliente.formaPagoHabitual || '30dias',
        moneda: cliente.monedaPreferida || 'CLP',
        sitioEntrega: cliente.sitiosHabituales[0] || ''
      }));

      // Calcular margen sugerido
      const margenNuevo = calcularMargenSugerido(cliente, formData.urgencia);
      setMargenSugerido(margenNuevo);
      setNuevoProducto(prev => ({ ...prev, margen: margenNuevo }));

      // Limpiar validaciones previas al cambiar de cliente
      setValidaciones([]);
    }
  };

  // Función para manejar cambio de urgencia
  const handleUrgenciaChange = (urgencia) => {
    setFormData(prev => ({ ...prev, urgencia }));

    if (urgencia === 'parada') {
      // Modo emergencia
      setFormData(prev => ({
        ...prev,
        diasPago: 'contado',
        metodoPago: 'transferencia'
      }));

      // Actualizar margen sugerido
      const margenNuevo = calcularMargenSugerido(clienteSeleccionado, urgencia);
      setMargenSugerido(margenNuevo);
      setNuevoProducto(prev => ({ ...prev, margen: margenNuevo }));
    }
  };

  useEffect(() => {
    calcularTotales();
  }, [formData.productos, formData.sitioEntrega, formData.fleteConIva]);

  const calcularPrecioVenta = () => {
    const costoNum = parseFloat(nuevoProducto.costo) || 0;
    const margenNum = parseFloat(nuevoProducto.margen) || 0;
    let precioSinIva = costoNum * (1 + margenNum / 100);
    let montoIva = 0;
    let precio = precioSinIva;

    if (nuevoProducto.ivaExtra) {
      // IVA se suma al precio
      montoIva = precioSinIva * 0.19;
      precio = precioSinIva + montoIva;
    } else {
      // IVA está incluido en el precio
      // Precio = PrecioNeto * 1.19, entonces PrecioNeto = Precio / 1.19
      // IVA = Precio - PrecioNeto
      montoIva = precioSinIva - (precioSinIva / 1.19);
    }

    // Redondear a 2 decimales
    precio = Math.round(precio * 100) / 100;
    montoIva = Math.round(montoIva * 100) / 100;

    setNuevoProducto(prev => ({ ...prev, precio, montoIva }));
  };

  useEffect(() => {
    calcularPrecioVenta();
  }, [nuevoProducto.costo, nuevoProducto.margen, nuevoProducto.ivaExtra]);

  const calcularTotales = (fleteOverride = null) => {
    const subtotal = formData.productos.reduce((sum, p) =>
      sum + (p.cantidad * p.precio), 0
    );

    const margenTotal = formData.productos.reduce((sum, p) =>
      sum + (p.cantidad * (p.precio - p.costo)), 0
    );

    // Usar el flete override si se proporciona, sino usar el actual
    let flete = fleteOverride !== null ? fleteOverride : totales.flete;

    // Solo calcular flete automático si no es manual y no hay override
    if (!fleteManual && formData.sitioEntrega && fleteOverride === null) {
      flete = calcularFleteInteligente(formData.sitioEntrega, formData.productos);
    }

    // Calcular IVA de productos siempre
    const ivaProductos = subtotal * 0.19;

    // Calcular IVA del flete según configuración
    let ivaFlete = 0;
    let total = 0;

    if (flete > 0) {
      if (formData.fleteConIva) {
        // Si el IVA ESTÁ incluido en el flete, calcular cuánto IVA hay dentro
        ivaFlete = flete - (flete / 1.19);
        // Total: productos + IVA productos + flete (que ya incluye IVA)
        total = subtotal + ivaProductos + flete;
      } else {
        // Si el IVA NO está incluido en el flete, calcularlo como adicional
        ivaFlete = flete * 0.19;
        // Total: productos + flete + todo el IVA
        total = subtotal + flete + ivaProductos + ivaFlete;
      }
    } else {
      // Sin flete, solo productos + IVA productos
      total = subtotal + ivaProductos;
    }

    const iva = ivaProductos + ivaFlete;

    setTotales(prev => ({
      ...prev,
      subtotal,
      margenTotal,
      flete: fleteOverride !== null ? fleteOverride : (fleteManual ? prev.flete : flete),
      iva,
      total
    }));
  };

  const agregarProducto = () => {
    if (nuevoProducto.codigo && nuevoProducto.descripcion && nuevoProducto.cantidad > 0) {
      setFormData(prev => ({
        ...prev,
        productos: [...prev.productos, { ...nuevoProducto, id: Date.now() }]
      }));

      setNuevoProducto({
        codigo: '',
        descripcion: '',
        cantidad: 1,
        unidad: 'UN',
        costo: 0,
        margen: margenSugerido,
        precio: 0,
        montoIva: 0,
        proveedor: '',
        leadTime: 15,
        ivaExtra: false
      });
    }
  };

  const eliminarProducto = (id) => {
    setFormData(prev => ({
      ...prev,
      productos: prev.productos.filter(p => p.id !== id)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Tasas de cambio
  const exchangeRates = {
    CLP: 1,
    USD: 950,
    UF: 37500
  };

  const formatCurrency = (value, currency = formData.moneda, hideZero = false) => {
    const amount = Math.round(value);

    // Ocultar si es 0 y hideZero es true
    if (hideZero && amount === 0) {
      return '';
    }

    if (currency === 'CLP') {
      return `$${amount.toLocaleString('es-CL')}`;
    } else if (currency === 'USD') {
      const usdAmount = amount / exchangeRates.USD;
      return `US$${usdAmount.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (currency === 'UF') {
      const ufAmount = amount / exchangeRates.UF;
      return `${ufAmount.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} UF`;
    }
    return `$${amount.toLocaleString('es-CL')}`;
  };

  const validarCotizacion = () => {
    const errores = [];
    const MARGEN_MINIMO_GLOBAL = 8;

    // Margen mínimo
    formData.productos.forEach(p => {
      if (p.margen < MARGEN_MINIMO_GLOBAL) {
        errores.push(`Margen inferior al mínimo (${MARGEN_MINIMO_GLOBAL}%) en ${p.descripcion}. Requiere aprobación gerencial.`);
      }
      if (p.precio < p.costo) {
        errores.push(`Precio de venta menor al costo en ${p.descripcion}`);
      }
    });

    // Validación Ley 30 días
    if (formData.diasPago !== 'contado' && formData.diasPago !== '30dias' && formData.diasPago !== '7dias' && formData.diasPago !== '15dias' && clienteSeleccionado?.categoria !== 'A') {
      errores.push('Plazo de pago excede Ley 30 días. Requiere acuerdo especial.');
    }

    // Lead time vs urgencia
    const maxLeadTime = Math.max(...formData.productos.map(p => p.leadTime || 0));
    if (formData.urgencia === 'urgente' && maxLeadTime > 7) {
      errores.push(`Lead time (${maxLeadTime} días) incompatible con urgencia`);
    }

    // Documentación requerida
    if (clienteSeleccionado?.requiereCertificaciones && formData.productos.length > 0) {
      errores.push(`Cliente requiere certificaciones: ${clienteSeleccionado.certificaciones.join(', ')}`);
    }

    // Crédito disponible
    if (clienteSeleccionado) {
      const creditoDisponible = clienteSeleccionado.credito - clienteSeleccionado.deudaActual;
      if (totales.total > creditoDisponible) {
        errores.push(`Cotización excede crédito disponible (${formatCurrency(creditoDisponible)})`);
      }
    }

    return errores;
  };

  const generarPDF = () => {
    const doc = new jsPDF();

    // Configuración inicial
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header - Logo y título con datos de la empresa
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(empresaActiva?.razonSocial || 'PAPER BOAT', 20, yPosition);
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(empresaActiva?.giro || 'Sistema de Gestión de Órdenes', 20, yPosition + 8);

    if (empresaActiva?.rut) {
      doc.text(`RUT: ${empresaActiva.rut}`, 20, yPosition + 16);
    }
    if (empresaActiva?.direccion) {
      doc.setFontSize(10);
      doc.text(empresaActiva.direccion, 20, yPosition + 24);
      doc.text(`Tel: ${empresaActiva.telefono || 'N/A'} - Email: ${empresaActiva.email || 'N/A'}`, 20, yPosition + 30);
    }

    // Fecha actual
    const fechaActual = new Date().toLocaleDateString('es-CL');
    doc.text(`Fecha: ${fechaActual}`, pageWidth - 60, yPosition);

    yPosition += empresaActiva?.direccion ? 40 : 25;

    // Título de cotización
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('COTIZACIÓN', 20, yPosition);
    yPosition += 15;

    // Información del cliente
    if (clienteSeleccionado) {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('INFORMACIÓN DEL CLIENTE', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Cliente: ${clienteSeleccionado.nombre}`, 20, yPosition);
      doc.text(`RUT: ${clienteSeleccionado.rut}`, 20, yPosition + 6);
      if (formData.email) doc.text(`Email: ${formData.email}`, 20, yPosition + 12);
      if (formData.telefono) doc.text(`Teléfono: ${formData.telefono}`, 20, yPosition + 18);
      if (formData.sitioEntrega) doc.text(`Sitio de Entrega: ${formData.sitioEntrega}`, 20, yPosition + 24);

      yPosition += 35;
    }

    // Productos
    if (formData.productos.length > 0) {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('PRODUCTOS Y SERVICIOS', 20, yPosition);
      yPosition += 10;

      // Cabecera de tabla
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.text('Código', 20, yPosition);
      doc.text('Descripción', 50, yPosition);
      doc.text('Cant.', 120, yPosition);
      doc.text('Costo', 135, yPosition);
      doc.text('Margen', 155, yPosition);
      doc.text('Precio', 175, yPosition);

      // Línea separadora
      doc.line(20, yPosition + 2, pageWidth - 20, yPosition + 2);
      yPosition += 8;

      // Productos
      doc.setFont(undefined, 'normal');
      formData.productos.forEach((producto, index) => {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }

        doc.text(producto.codigo, 20, yPosition);

        // Descripción con wrap
        const descripcion = producto.descripcion.length > 30
          ? producto.descripcion.substring(0, 30) + '...'
          : producto.descripcion;
        doc.text(descripcion, 50, yPosition);

        doc.text(producto.cantidad.toString(), 120, yPosition);
        doc.text(formatCurrency(producto.costo, formData.moneda), 135, yPosition);
        doc.text(`${producto.margen}%`, 155, yPosition);
        doc.text(formatCurrency(producto.precio, formData.moneda), 175, yPosition);

        yPosition += 6;
      });

      yPosition += 10;
    }

    // Totales
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('RESUMEN FINANCIERO', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Subtotal: ${formatCurrency(totales.subtotal, formData.moneda)}`, 20, yPosition);
    doc.text(`Margen Total: ${formatCurrency(totales.margenTotal, formData.moneda)}`, 20, yPosition + 6);
    if (totales.flete > 0) {
      doc.text(`Flete: ${formatCurrency(totales.flete, formData.moneda)}`, 20, yPosition + 12);
      yPosition += 6;
    }
    doc.text(`IVA (19%): ${formatCurrency(totales.iva, formData.moneda)}`, 20, yPosition + 12);

    // Total final destacado
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(`TOTAL: ${formatCurrency(totales.total, formData.moneda)}`, 20, yPosition + 22);

    yPosition += 35;

    // Condiciones comerciales
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('CONDICIONES COMERCIALES', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Moneda: ${formData.moneda}`, 20, yPosition);
    doc.text(`Método de Pago: ${formData.metodoPago}`, 20, yPosition + 6);
    doc.text(`Días de Pago: ${formData.diasPago}`, 20, yPosition + 12);
    doc.text(`Validez: ${formData.validez} días`, 20, yPosition + 18);

    if (formData.incluyeInstalacion || formData.incluyeCapacitacion) {
      yPosition += 30;
      doc.text('Servicios Incluidos:', 20, yPosition);
      if (formData.incluyeInstalacion) doc.text('• Instalación', 25, yPosition + 6);
      if (formData.incluyeCapacitacion) doc.text('• Capacitación', 25, yPosition + (formData.incluyeInstalacion ? 12 : 6));
    }

    // Footer
    const footerY = pageHeight - 20;
    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.text(`${empresaActiva?.razonSocial || 'PAPER BOAT'} - ${empresaActiva?.giro || 'Sistema de Gestión de Órdenes'}`, pageWidth / 2, footerY, { align: 'center' });

    // Abrir PDF en nueva ventana
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  };

  const handleCrearCotizacion = () => {
    const errores = validarCotizacion();

    if (errores.length > 0) {
      alert('⚠️ Validaciones pendientes:\n\n' + errores.join('\n\n'));
      return;
    }

    // Aquí iría la lógica para crear la cotización
    console.log('Crear cotización:', formData);
    alert('✅ Cotización creada exitosamente');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <Plus size={24} />
            <div>
              <h2>Nueva Cotización</h2>
              {empresaActiva && (
                <p style={{ fontSize: '12px', opacity: '0.8', margin: '2px 0 0 0' }}>
                  {empresaActiva.nombreFantasia} - {empresaActiva.monedaDefault}
                </p>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c4fffc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* Panel de Estado */}
          {validaciones.length > 0 && (
            <div className="panel-estado">
              <div className="panel-header">
                <Info size={18} />
                <h4>Estado de la Cotización</h4>
              </div>
              <div className="validaciones-list">
                {validaciones.map((val, idx) => (
                  <div key={idx} className={`validacion-item ${val.tipo}`}>
                    <span>{val.mensaje}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Información del Cliente */}
          <div className="form-section">
            <div className="section-header">
              <Building size={20} />
              <h3>Información del Cliente</h3>
            </div>

            <div className="form-grid">
              <div className="form-group full-width">
                <label>Cliente *</label>
                <select
                  value={formData.cliente}
                  onChange={(e) => handleClienteChange(e.target.value)}
                >
                  <option value="">Seleccionar o buscar por RUT/Nombre</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre} - RUT: {cliente.rut} (Cat. {cliente.categoria})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Contacto *</label>
                <div className="input-with-button">
                  <select
                    value={formData.contacto}
                    onChange={(e) => {
                      const contacto = clienteSeleccionado?.contactos.find(c => c.id === parseInt(e.target.value));
                      if (contacto) {
                        handleInputChange('contacto', e.target.value);
                        handleInputChange('email', contacto.email);
                        handleInputChange('telefono', contacto.telefono);
                      }
                    }}
                    disabled={!clienteSeleccionado}
                  >
                    <option value="">
                      {clienteSeleccionado
                        ? `Seleccionar contacto (${clienteSeleccionado.contactos?.length || 0} disponibles)`
                        : "Primero seleccione un cliente"}
                    </option>
                    {clienteSeleccionado?.contactos?.map(contacto => (
                      <option key={contacto.id} value={contacto.id}>
                        {contacto.nombre} - {contacto.cargo}
                      </option>
                    ))}
                  </select>
                  {clienteSeleccionado && (
                    <button
                      type="button"
                      className="btn-add-contact"
                      onClick={() => console.log('Agregar nuevo contacto')}
                      title="Agregar nuevo contacto"
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <div className="input-with-icon">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="email@empresa.cl"
                  />
                  <Mail size={18} />
                </div>
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <div className="input-with-icon">
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                    placeholder="+56 9 XXXX XXXX"
                  />
                  <Phone size={18} />
                </div>
              </div>

              <div className="form-group">
                <label>Sitio de Entrega</label>
                <select
                  value={formData.sitioEntrega}
                  onChange={(e) => handleInputChange('sitioEntrega', e.target.value)}
                  disabled={!clienteSeleccionado}
                >
                  <option value="">
                    {clienteSeleccionado
                      ? "Seleccionar sitio de entrega"
                      : "Primero seleccione un cliente"}
                  </option>
                  {clienteSeleccionado?.sitiosHabituales?.map(sitio => (
                    <option key={sitio} value={sitio}>{sitio}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Pedido</label>
                <div className="checkbox-grid-pedido">
                  <div className="checkbox-option" onClick={() => handleUrgenciaChange('normal')}>
                    <input
                      type="checkbox"
                      checked={formData.urgencia === 'normal'}
                      onChange={() => handleUrgenciaChange('normal')}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Normal</span>
                  </div>
                  <div className="checkbox-option" onClick={() => handleUrgenciaChange('urgente')}>
                    <input
                      type="checkbox"
                      checked={formData.urgencia === 'urgente'}
                      onChange={() => handleUrgenciaChange('urgente')}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="urgente">Urgente</span>
                  </div>
                  <div className="checkbox-option" onClick={() => handleUrgenciaChange('parada')}>
                    <input
                      type="checkbox"
                      checked={formData.urgencia === 'parada'}
                      onChange={() => handleUrgenciaChange('parada')}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="parada">Parada de Planta</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Productos/Servicios */}
          <div className="form-section">
            <div className="section-header">
              <Package size={20} />
              <h3>Productos / Servicios</h3>
            </div>

            <div className="producto-add-container">
              <div className="producto-form-grid">
                <div className="form-group">
                  <label>Código/SKU</label>
                  <input
                    type="text"
                    value={nuevoProducto.codigo}
                    onChange={(e) => setNuevoProducto(prev => ({ ...prev, codigo: e.target.value }))}
                    placeholder="Ej: PRD-001"
                  />
                </div>
                <div className="form-group">
                  <label>Descripción del producto</label>
                  <input
                    type="text"
                    value={nuevoProducto.descripcion}
                    onChange={(e) => setNuevoProducto(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Descripción detallada"
                  />
                </div>
                <div className="form-group">
                  <label>Cantidad</label>
                  <input
                    type="number"
                    value={nuevoProducto.cantidad}
                    onChange={(e) => setNuevoProducto(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 0 }))}
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label>Unidad</label>
                  <select
                    value={nuevoProducto.unidad}
                    onChange={(e) => setNuevoProducto(prev => ({ ...prev, unidad: e.target.value }))}
                  >
                    <option value="UN">UN</option>
                    <option value="MT">MT</option>
                    <option value="KG">KG</option>
                    <option value="SET">SET</option>
                    <option value="GL">GL</option>
                    <option value="LT">LT</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Proveedor</label>
                  <select
                    value={nuevoProducto.proveedor}
                    onChange={(e) => {
                      const proveedor = proveedores.find(p => p.nombre === e.target.value);
                      setNuevoProducto(prev => ({
                        ...prev,
                        proveedor: e.target.value,
                        leadTime: proveedor?.leadTimeBase || 15
                      }));
                    }}
                  >
                    <option value="">Seleccionar proveedor</option>
                    {proveedores.map(prov => (
                      <option key={prov.nombre} value={prov.nombre}>
                        {prov.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Costo ({formData.moneda})</label>
                  <div className="input-with-currency">
                    <span className="currency-symbol">{formData.moneda === 'USD' ? 'US$' : formData.moneda === 'UF' ? 'UF' : '$'}</span>
                    <input
                      type="text"
                      value={nuevoProducto.costo > 0 ? nuevoProducto.costo.toLocaleString('es-CL') : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        const numValue = parseFloat(value) || 0;
                        setNuevoProducto(prev => ({ ...prev, costo: numValue }));
                      }}
                      placeholder={formData.moneda === 'CLP' ? '1.000.000' : formData.moneda === 'USD' ? '1,000.00' : '26.754'}
                      style={{ paddingLeft: '35px' }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>IVA (19%)</label>
                  <select
                    value={nuevoProducto.ivaExtra ? 'extra' : 'incluido'}
                    onChange={(e) => setNuevoProducto(prev => ({ ...prev, ivaExtra: e.target.value === 'extra' }))}
                    style={{
                      background: '#000',
                      color: '#c4fffc',
                      border: '1px solid #c4fffc',
                      padding: '5px',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="incluido">IVA incluido en costo</option>
                    <option value="extra">IVA se suma (+19%)</option>
                  </select>
                </div>

                {nuevoProducto.ivaExtra === false && nuevoProducto.costo > 0 && (
                  <div className="form-group">
                    <label>Costo Neto</label>
                    <input
                      type="text"
                      value={formatCurrency(nuevoProducto.costo / 1.19, formData.moneda, true)}
                      readOnly
                      className="readonly"
                      style={{ background: '#0a0a0a', cursor: 'not-allowed', color: '#c4fffc', fontSize: '14px' }}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Monto IVA</label>
                  <input
                    type="text"
                    value={formatCurrency(nuevoProducto.montoIva || 0, formData.moneda, true)}
                    readOnly
                    className="readonly"
                    style={{ background: '#0a0a0a', cursor: 'not-allowed', color: '#FFA726' }}
                    placeholder={nuevoProducto.costo > 0 ? 'Calculando...' : 'Ingrese costo'}
                  />
                </div>
                <div className="form-group">
                  <label>Margen %</label>
                  <input
                    type="text"
                    value={nuevoProducto.margen > 0 ? nuevoProducto.margen : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      const numValue = parseFloat(value) || 0;
                      setNuevoProducto(prev => ({ ...prev, margen: numValue }));
                    }}
                    placeholder="18"
                  />
                  {nuevoProducto.costo > 0 && nuevoProducto.margen > 0 && (
                    <small style={{ color: '#c4fffc', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                      Margen: {formatCurrency((nuevoProducto.costo * nuevoProducto.margen / 100), formData.moneda)}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Precio Venta Final</label>
                  <input
                    type="text"
                    value={formatCurrency(nuevoProducto.precio, formData.moneda, true)}
                    readOnly
                    className="readonly"
                    style={{ background: '#0a0a0a', cursor: 'not-allowed', color: '#66FF66', fontWeight: 'bold' }}
                    placeholder={nuevoProducto.costo > 0 ? 'Calculando...' : 'Ingrese costo y margen'}
                  />
                  {nuevoProducto.costo > 0 && (
                    <small style={{ color: '#c4fffc', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                      {nuevoProducto.ivaExtra
                        ? `Neto: ${formatCurrency(nuevoProducto.precio / 1.19, formData.moneda)} + IVA: ${formatCurrency(nuevoProducto.montoIva || 0, formData.moneda)}`
                        : `Precio con IVA incluido (Neto: ${formatCurrency(nuevoProducto.precio / 1.19, formData.moneda)})`
                      }
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Tiempo de Entrega</label>
                  <input
                    type="number"
                    value={nuevoProducto.leadTime}
                    onChange={(e) => setNuevoProducto(prev => ({ ...prev, leadTime: parseInt(e.target.value) || 0 }))}
                    placeholder="Días para entrega"
                  />
                </div>
              </div>
              <button className="btn-add-product-full" onClick={agregarProducto}>
                <Plus size={18} />
                Agregar Producto
              </button>
            </div>

            {formData.productos.length > 0 && (
              <div className="productos-lista">
                <table>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descripción</th>
                      <th>Cant.</th>
                      <th>Unidad</th>
                      <th>Proveedor</th>
                      <th>Costo</th>
                      <th>Margen</th>
                      <th>Precio</th>
                      <th>Subtotal</th>
                      <th>Lead Time</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.productos.map(producto => (
                      <tr key={producto.id}>
                        <td>{producto.codigo}</td>
                        <td title={producto.descripcion}>
                          {producto.descripcion.length > 25
                            ? `${producto.descripcion.substring(0, 25)}...`
                            : producto.descripcion}
                        </td>
                        <td>{producto.cantidad}</td>
                        <td>{producto.unidad}</td>
                        <td>{producto.proveedor || '-'}</td>
                        <td>{formatCurrency(producto.costo, formData.moneda)}</td>
                        <td className={producto.margen < 10 ? 'margen-bajo' : ''}>
                          {producto.margen}%
                        </td>
                        <td>{formatCurrency(producto.precio, formData.moneda)}</td>
                        <td>{formatCurrency(producto.cantidad * producto.precio, formData.moneda)}</td>
                        <td>{producto.leadTime} días</td>
                        <td>
                          <button
                            className="btn-delete"
                            onClick={() => eliminarProducto(producto.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Condiciones Comerciales */}
          <div className="form-section">
            <div className="section-header">
              <FileText size={20} />
              <h3>Condiciones Comerciales</h3>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Moneda</label>
                <select
                  value={formData.moneda}
                  onChange={(e) => handleInputChange('moneda', e.target.value)}
                >
                  <option value="CLP">CLP - Peso Chileno</option>
                  <option value="USD">USD - Dólar</option>
                  <option value="UF">UF - Unidad de Fomento</option>
                </select>
              </div>

              <div className="form-group">
                <label>Método de Pago</label>
                <select
                  value={formData.metodoPago}
                  onChange={(e) => handleInputChange('metodoPago', e.target.value)}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="deposito">Depósito Bancario</option>
                  <option value="cheque">Cheque</option>
                  <option value="tarjeta">Tarjeta de Crédito/Débito</option>
                </select>
              </div>

              <div className="form-group">
                <label>Días de Pago</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    value={formData.diasPago}
                    onChange={(e) => handleInputChange('diasPago', e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="contado">Contado (0 días)</option>
                    <option value="7dias">7 días</option>
                    <option value="15dias">15 días</option>
                    <option value="30dias">30 días</option>
                    <option value="45dias">45 días</option>
                    <option value="60dias">60 días</option>
                    <option value="90dias">90 días</option>
                    <option value="120dias">120 días</option>
                    <option value="manual">Manual</option>
                  </select>
                  {formData.diasPago === 'manual' && (
                    <input
                      type="number"
                      placeholder="Días"
                      min="0"
                      value={formData.diasPagoCustom || ''}
                      onChange={(e) => handleInputChange('diasPagoCustom', e.target.value)}
                      style={{ width: '80px', background: '#000', color: '#c4fffc', border: '1px solid #c4fffc', padding: '5px', borderRadius: '4px' }}
                    />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Validez de la Cotización (días)</label>
                <input
                  type="number"
                  value={formData.validez}
                  onChange={(e) => handleInputChange('validez', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Servicios Incluidos</label>
                <div className="checkbox-grid-pedido">
                  <div className="checkbox-option" onClick={() => handleInputChange('incluyeInstalacion', !formData.incluyeInstalacion)}>
                    <input
                      type="checkbox"
                      checked={formData.incluyeInstalacion}
                      onChange={() => handleInputChange('incluyeInstalacion', !formData.incluyeInstalacion)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Instalación</span>
                  </div>
                  <div className="checkbox-option" onClick={() => handleInputChange('incluyeCapacitacion', !formData.incluyeCapacitacion)}>
                    <input
                      type="checkbox"
                      checked={formData.incluyeCapacitacion}
                      onChange={() => handleInputChange('incluyeCapacitacion', !formData.incluyeCapacitacion)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Capacitación</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Traslado */}
          <div className="form-section calculo-section">
            <div className="section-header">
              <Truck size={20} />
              <h3>Traslado</h3>
            </div>

            <div className="totales-grid">
              <div className="total-item editable">
                <span>Costo de Flete:</span>
                <div className="flete-input">
                  <span>{formData.moneda === 'USD' ? 'US$' : formData.moneda === 'UF' ? 'UF' : '$'}</span>
                  <input
                    type="text"
                    value={totales.flete}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      const newFlete = parseFloat(value) || 0;
                      setFleteManual(true);
                      // Calcular totales con el nuevo flete
                      calcularTotales(newFlete);
                    }}
                    placeholder="0"
                    style={{
                      appearance: 'none',
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none'
                    }}
                  />
                </div>
              </div>

              {formData.fleteConIva && totales.flete > 0 && (
                <div className="total-item">
                  <span>Flete Neto:</span>
                  <span className="total-value" style={{ color: '#c4fffc', fontSize: '14px' }}>
                    {formatCurrency(totales.flete / 1.19, formData.moneda)}
                  </span>
                </div>
              )}

              <div className="total-item">
                <label>IVA en Flete</label>
                <select
                  value={formData.fleteConIva ? 'incluido' : 'extra'}
                  onChange={(e) => {
                    const fleteConIva = e.target.value === 'incluido';
                    setFormData(prev => ({ ...prev, fleteConIva }));
                    // Recalcular totales con la nueva configuración
                    setTimeout(() => calcularTotales(totales.flete), 0);
                  }}
                  style={{
                    background: '#000',
                    color: '#c4fffc',
                    border: '1px solid #c4fffc',
                    padding: '5px',
                    borderRadius: '4px'
                  }}
                >
                  <option value="extra">IVA se suma (+19%)</option>
                  <option value="incluido">IVA incluido en flete</option>
                </select>
              </div>
              <div className="total-item">
                <span>IVA Total:</span>
                <span className="total-value">{formatCurrency(totales.iva, formData.moneda)}</span>
              </div>
              <div className="total-item total-final">
                <span>TOTAL COTIZACIÓN:</span>
                <span className="total-value">{formatCurrency(totales.total, formData.moneda)}</span>
              </div>
            </div>

            {formData.urgencia === 'parada' && (
              <div className="alert alert-warning">
                <AlertCircle size={18} />
                <span>Parada de planta: Se sugiere aplicar margen adicional del 5%</span>
              </div>
            )}
          </div>

          {/* Notas y Documentos */}
          <div className="form-section">
            <div className="section-header">
              <FileText size={20} />
              <h3>Notas y Documentos</h3>
            </div>

            <div className="form-grid">
              <div className="form-group full-width">
                <label>Observaciones internas (no visible al cliente)</label>
                <textarea
                  value={formData.observacionesInternas}
                  onChange={(e) => handleInputChange('observacionesInternas', e.target.value)}
                  rows="3"
                  placeholder="Notas para uso interno..."
                />
              </div>

              <div className="form-group full-width">
                <label>Condiciones para el cliente</label>
                <textarea
                  value={formData.condicionesCliente}
                  onChange={(e) => handleInputChange('condicionesCliente', e.target.value)}
                  rows="3"
                  placeholder="Condiciones especiales, garantías, etc..."
                />
              </div>

              <div className="form-group full-width">
                <label>Adjuntar documentos</label>
                <div className="file-upload">
                  <button className="btn-attach">
                    <Paperclip size={18} />
                    Ficha técnica
                  </button>
                  <button className="btn-attach">
                    <Paperclip size={18} />
                    Certificados
                  </button>
                  <button className="btn-attach">
                    <Paperclip size={18} />
                    Otros
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            <Save size={18} />
            Guardar Borrador
          </button>
          <button className="btn-secondary" onClick={generarPDF}>
            <Eye size={18} />
            Vista Previa PDF
          </button>
          <button className="btn-secondary">
            <Send size={18} />
            Enviar por Email
          </button>
          <button className="btn-primary" onClick={handleCrearCotizacion}>
            <Plus size={18} />
            Crear Cotización
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalNuevaCotizacion;